/**
 * Centralized Error Handling Utility
 *
 * Standardizes error handling across all services with:
 * - Error categorization (network, auth, validation, unknown)
 * - User-friendly messages
 * - Retry logic for transient failures
 * - Structured logging integration
 * - Circuit breaker pattern for cascading failure prevention
 * - Error metrics collection for monitoring
 * - Request correlation for tracing
 * - Batch error handling for bulk operations
 *
 * Optimized: January 20, 2026
 */

import { logger } from '../lib/logger';

// Error categories for different handling strategies
export enum ErrorCategory {
    NETWORK = 'NETWORK',
    AUTH = 'AUTH',
    VALIDATION = 'VALIDATION',
    NOT_FOUND = 'NOT_FOUND',
    PERMISSION = 'PERMISSION',
    RATE_LIMIT = 'RATE_LIMIT',
    TIMEOUT = 'TIMEOUT',
    CONFLICT = 'CONFLICT',
    SERVER_ERROR = 'SERVER_ERROR',
    CIRCUIT_OPEN = 'CIRCUIT_OPEN',
    UNKNOWN = 'UNKNOWN',
}

// Detailed error codes for more granular identification
export enum ErrorCode {
    // Network errors
    NETWORK_OFFLINE = 'NETWORK_OFFLINE',
    NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
    NETWORK_DNS = 'NETWORK_DNS',
    NETWORK_SSL = 'NETWORK_SSL',

    // Auth errors
    AUTH_EXPIRED = 'AUTH_EXPIRED',
    AUTH_INVALID_TOKEN = 'AUTH_INVALID_TOKEN',
    AUTH_REFRESH_FAILED = 'AUTH_REFRESH_FAILED',

    // Validation errors
    VALIDATION_REQUIRED = 'VALIDATION_REQUIRED',
    VALIDATION_FORMAT = 'VALIDATION_FORMAT',
    VALIDATION_CONSTRAINT = 'VALIDATION_CONSTRAINT',

    // Database errors
    DB_CONNECTION = 'DB_CONNECTION',
    DB_CONSTRAINT = 'DB_CONSTRAINT',
    DB_CONFLICT = 'DB_CONFLICT',

    // Rate limiting
    RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
    RATE_LIMIT_BURST = 'RATE_LIMIT_BURST',

    // Server errors
    SERVER_INTERNAL = 'SERVER_INTERNAL',
    SERVER_UNAVAILABLE = 'SERVER_UNAVAILABLE',
    SERVER_BAD_GATEWAY = 'SERVER_BAD_GATEWAY',

    // Circuit breaker
    CIRCUIT_OPEN = 'CIRCUIT_OPEN',
    CIRCUIT_HALF_OPEN = 'CIRCUIT_HALF_OPEN',

    // Unknown
    UNKNOWN = 'UNKNOWN',
}

// Standardized error structure
export interface AppError {
    category: ErrorCategory;
    code: ErrorCode;           // Detailed error code
    message: string;           // User-friendly message
    technicalMessage: string;  // Original error for debugging
    retryable: boolean;
    statusCode?: number;
    correlationId?: string;    // Request correlation ID
    timestamp: number;         // Error timestamp
    context?: string;          // Error context (service/method)
    metadata?: Record<string, unknown>; // Additional error metadata
    originalError?: unknown;
}

// User-friendly messages by category
const USER_MESSAGES: Record<ErrorCategory, string> = {
    [ErrorCategory.NETWORK]: 'Connection error. Please check your internet and try again.',
    [ErrorCategory.AUTH]: 'Your session has expired. Please sign in again.',
    [ErrorCategory.VALIDATION]: 'Please check your input and try again.',
    [ErrorCategory.NOT_FOUND]: 'The requested item could not be found.',
    [ErrorCategory.PERMISSION]: 'You don\'t have permission to perform this action.',
    [ErrorCategory.RATE_LIMIT]: 'Too many requests. Please wait a moment and try again.',
    [ErrorCategory.TIMEOUT]: 'The request took too long. Please try again.',
    [ErrorCategory.CONFLICT]: 'This action conflicts with another operation. Please refresh and try again.',
    [ErrorCategory.SERVER_ERROR]: 'Server error. Our team has been notified.',
    [ErrorCategory.CIRCUIT_OPEN]: 'Service temporarily unavailable. Please try again in a few moments.',
    [ErrorCategory.UNKNOWN]: 'Something went wrong. Please try again.',
};

// ============================================================================
// CIRCUIT BREAKER IMPLEMENTATION
// ============================================================================

export enum CircuitState {
    CLOSED = 'CLOSED',       // Normal operation
    OPEN = 'OPEN',           // Failures exceeded threshold, blocking requests
    HALF_OPEN = 'HALF_OPEN', // Testing if service recovered
}

interface CircuitBreakerState {
    state: CircuitState;
    failures: number;
    lastFailure: number;
    lastSuccess: number;
    successesSinceHalfOpen: number;
}

interface CircuitBreakerConfig {
    failureThreshold: number;      // Failures before opening circuit (default: 5)
    resetTimeoutMs: number;        // Time before trying half-open (default: 30000)
    halfOpenSuccessThreshold: number; // Successes needed to close (default: 2)
}

const DEFAULT_CIRCUIT_CONFIG: CircuitBreakerConfig = {
    failureThreshold: 5,
    resetTimeoutMs: 30000,
    halfOpenSuccessThreshold: 2,
};

// Circuit breakers by service name
const circuitBreakers = new Map<string, CircuitBreakerState>();
const MAX_CIRCUIT_BREAKERS = 50;

/**
 * Get or create circuit breaker state for a service
 */
function getCircuitState(serviceName: string): CircuitBreakerState {
    if (!circuitBreakers.has(serviceName)) {
        // Evict oldest entry if at capacity
        if (circuitBreakers.size >= MAX_CIRCUIT_BREAKERS) {
            let oldestKey = '';
            let oldestTime = Infinity;
            for (const [key, state] of circuitBreakers) {
                const lastActivity = Math.max(state.lastFailure, state.lastSuccess);
                if (lastActivity < oldestTime) {
                    oldestTime = lastActivity;
                    oldestKey = key;
                }
            }
            if (oldestKey) {
                circuitBreakers.delete(oldestKey);
            }
        }
        circuitBreakers.set(serviceName, {
            state: CircuitState.CLOSED,
            failures: 0,
            lastFailure: 0,
            lastSuccess: Date.now(),
            successesSinceHalfOpen: 0,
        });
    }
    return circuitBreakers.get(serviceName)!;
}

/**
 * Check if circuit allows request
 */
function canMakeRequest(serviceName: string, config: CircuitBreakerConfig = DEFAULT_CIRCUIT_CONFIG): boolean {
    const circuit = getCircuitState(serviceName);

    switch (circuit.state) {
        case CircuitState.CLOSED:
            return true;

        case CircuitState.OPEN:
            // Check if reset timeout has passed
            if (Date.now() - circuit.lastFailure >= config.resetTimeoutMs) {
                circuit.state = CircuitState.HALF_OPEN;
                circuit.successesSinceHalfOpen = 0;
                logger.info(`Circuit breaker half-open for: ${serviceName}`);
                return true;
            }
            return false;

        case CircuitState.HALF_OPEN:
            return true;
    }
}

/**
 * Record a successful request
 */
function recordSuccess(serviceName: string, config: CircuitBreakerConfig = DEFAULT_CIRCUIT_CONFIG): void {
    const circuit = getCircuitState(serviceName);
    circuit.lastSuccess = Date.now();

    if (circuit.state === CircuitState.HALF_OPEN) {
        circuit.successesSinceHalfOpen++;
        if (circuit.successesSinceHalfOpen >= config.halfOpenSuccessThreshold) {
            circuit.state = CircuitState.CLOSED;
            circuit.failures = 0;
            logger.info(`Circuit breaker closed for: ${serviceName}`);
        }
    } else if (circuit.state === CircuitState.CLOSED) {
        // Reset failure count on success
        circuit.failures = Math.max(0, circuit.failures - 1);
    }
}

/**
 * Record a failed request
 */
function recordFailure(serviceName: string, config: CircuitBreakerConfig = DEFAULT_CIRCUIT_CONFIG): void {
    const circuit = getCircuitState(serviceName);
    circuit.failures++;
    circuit.lastFailure = Date.now();

    if (circuit.state === CircuitState.HALF_OPEN) {
        // Immediately reopen on failure in half-open
        circuit.state = CircuitState.OPEN;
        logger.warn(`Circuit breaker reopened for: ${serviceName}`);
    } else if (circuit.state === CircuitState.CLOSED && circuit.failures >= config.failureThreshold) {
        circuit.state = CircuitState.OPEN;
        logger.warn(`Circuit breaker opened for: ${serviceName}`, {
            failures: circuit.failures,
            threshold: config.failureThreshold,
        });
    }
}

/**
 * Get circuit breaker status for a service
 */
export function getCircuitStatus(serviceName: string): CircuitBreakerState {
    return { ...getCircuitState(serviceName) };
}

/**
 * Get all circuit breaker statuses
 */
export function getAllCircuitStatuses(): Record<string, CircuitBreakerState> {
    const statuses: Record<string, CircuitBreakerState> = {};
    circuitBreakers.forEach((state, name) => {
        statuses[name] = { ...state };
    });
    return statuses;
}

/**
 * Reset circuit breaker for a service
 */
export function resetCircuit(serviceName: string): void {
    circuitBreakers.delete(serviceName);
    logger.info(`Circuit breaker reset for: ${serviceName}`);
}

/**
 * Reset all circuit breakers
 */
export function resetAllCircuits(): void {
    circuitBreakers.clear();
    logger.info('All circuit breakers reset');
}

// ============================================================================
// ERROR METRICS COLLECTION
// ============================================================================

interface ErrorMetrics {
    totalErrors: number;
    errorsByCategory: Record<ErrorCategory, number>;
    errorsByCode: Record<ErrorCode, number>;
    errorsByService: Record<string, number>;
    lastErrors: AppError[];         // Last N errors for debugging
    hourlyRates: number[];          // Errors per hour for last 24 hours
    lastHourIndex: number;          // Current hour index
    lastUpdate: number;             // Last update timestamp
}

const MAX_LAST_ERRORS = 100;
const HOURS_TO_TRACK = 24;

let errorMetrics: ErrorMetrics = {
    totalErrors: 0,
    errorsByCategory: {} as Record<ErrorCategory, number>,
    errorsByCode: {} as Record<ErrorCode, number>,
    errorsByService: {},
    lastErrors: [],
    hourlyRates: new Array(HOURS_TO_TRACK).fill(0),
    lastHourIndex: new Date().getHours(),
    lastUpdate: Date.now(),
};

/**
 * Record error in metrics
 */
function recordErrorMetric(error: AppError): void {
    const currentHour = new Date().getHours();

    // Reset hourly counter if we've moved to a new hour
    if (currentHour !== errorMetrics.lastHourIndex) {
        // Shift hourly rates
        const hoursElapsed = (currentHour - errorMetrics.lastHourIndex + 24) % 24;
        for (let i = 0; i < Math.min(hoursElapsed, HOURS_TO_TRACK); i++) {
            errorMetrics.hourlyRates[(errorMetrics.lastHourIndex + i + 1) % HOURS_TO_TRACK] = 0;
        }
        errorMetrics.lastHourIndex = currentHour;
    }

    errorMetrics.totalErrors++;
    errorMetrics.hourlyRates[currentHour]++;

    // Track by category
    errorMetrics.errorsByCategory[error.category] =
        (errorMetrics.errorsByCategory[error.category] || 0) + 1;

    // Track by code
    errorMetrics.errorsByCode[error.code] =
        (errorMetrics.errorsByCode[error.code] || 0) + 1;

    // Track by service/context
    if (error.context) {
        const service = error.context.split('.')[0];
        errorMetrics.errorsByService[service] =
            (errorMetrics.errorsByService[service] || 0) + 1;
    }

    // Keep last N errors
    errorMetrics.lastErrors.unshift(error);
    if (errorMetrics.lastErrors.length > MAX_LAST_ERRORS) {
        errorMetrics.lastErrors.pop();
    }

    errorMetrics.lastUpdate = Date.now();
}

/**
 * Get error metrics snapshot
 */
export function getErrorMetrics(): ErrorMetrics {
    return { ...errorMetrics };
}

/**
 * Get error rate for the last hour
 */
export function getHourlyErrorRate(): number {
    const currentHour = new Date().getHours();
    return errorMetrics.hourlyRates[currentHour];
}

/**
 * Get error rate for the last 24 hours
 */
export function getDailyErrorRate(): number {
    return errorMetrics.hourlyRates.reduce((sum, count) => sum + count, 0);
}

/**
 * Get top error categories
 */
export function getTopErrorCategories(limit: number = 5): Array<{ category: ErrorCategory; count: number }> {
    return Object.entries(errorMetrics.errorsByCategory)
        .map(([category, count]) => ({ category: category as ErrorCategory, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
}

/**
 * Get recent errors
 */
export function getRecentErrors(limit: number = 10): AppError[] {
    return errorMetrics.lastErrors.slice(0, limit);
}

/**
 * Reset error metrics
 */
export function resetErrorMetrics(): void {
    errorMetrics = {
        totalErrors: 0,
        errorsByCategory: {} as Record<ErrorCategory, number>,
        errorsByCode: {} as Record<ErrorCode, number>,
        errorsByService: {},
        lastErrors: [],
        hourlyRates: new Array(HOURS_TO_TRACK).fill(0),
        lastHourIndex: new Date().getHours(),
        lastUpdate: Date.now(),
    };
    logger.info('Error metrics reset');
}

// ============================================================================
// CORRELATION ID MANAGEMENT
// ============================================================================

/**
 * Generate a correlation ID
 */
export function generateCorrelationId(): string {
    return crypto.randomUUID();
}

/**
 * @deprecated Do not use module-level mutable state for per-request context.
 * Pass correlationId explicitly through function parameters instead.
 * This function is a no-op retained for backward compatibility.
 */
export function setCorrelationId(_id: string): void {
    // No-op: correlation IDs should be passed explicitly through the call chain.
    // Module-level mutable state causes race conditions in concurrent async code.
}

/**
 * @deprecated Do not use module-level mutable state for per-request context.
 * Pass correlationId explicitly through function parameters instead.
 * This function always returns null.
 */
export function getCorrelationId(): string | null {
    return null;
}

/**
 * @deprecated Do not use module-level mutable state for per-request context.
 * This function is a no-op retained for backward compatibility.
 */
export function clearCorrelationId(): void {
    // No-op: correlation IDs should be passed explicitly through the call chain.
}

/**
 * Categorize an error based on its properties
 * Returns both category and detailed error code
 */
function categorizeError(error: unknown): { category: ErrorCategory; code: ErrorCode; statusCode?: number } {
    // Handle Supabase-style errors
    if (error && typeof error === 'object') {
        const err = error as Record<string, unknown>;

        // Check for status/statusCode
        const status = (err.status ?? err.statusCode ?? err.code) as number | string | undefined;
        const statusNum = typeof status === 'number' ? status : parseInt(String(status), 10);

        // Check error message for patterns
        const message = String(err.message ?? err.error ?? '').toLowerCase();

        // Timeout errors (specific check before general network)
        if (message.includes('timeout') || message.includes('timed out') ||
            message.includes('deadline') || statusNum === 408 || statusNum === 504) {
            return { category: ErrorCategory.TIMEOUT, code: ErrorCode.NETWORK_TIMEOUT, statusCode: statusNum || 408 };
        }

        // Rate limiting
        if (statusNum === 429 || message.includes('rate limit') || message.includes('too many')) {
            const code = message.includes('burst') ? ErrorCode.RATE_LIMIT_BURST : ErrorCode.RATE_LIMIT_EXCEEDED;
            return { category: ErrorCategory.RATE_LIMIT, code, statusCode: 429 };
        }

        // Auth errors with detailed codes
        if (statusNum === 401 || statusNum === 403 ||
            message.includes('jwt') || message.includes('token') ||
            message.includes('auth') || message.includes('session') ||
            message.includes('unauthorized') || message.includes('forbidden')) {
            let code = ErrorCode.AUTH_EXPIRED;
            if (message.includes('invalid') || message.includes('malformed')) {
                code = ErrorCode.AUTH_INVALID_TOKEN;
            } else if (message.includes('refresh')) {
                code = ErrorCode.AUTH_REFRESH_FAILED;
            }
            return { category: ErrorCategory.AUTH, code, statusCode: statusNum || 401 };
        }

        // Not found
        if (statusNum === 404 || message.includes('not found') || message.includes('no rows')) {
            return { category: ErrorCategory.NOT_FOUND, code: ErrorCode.UNKNOWN, statusCode: 404 };
        }

        // Conflict errors
        if (statusNum === 409 || message.includes('conflict') || message.includes('already exists') ||
            message.includes('duplicate') || message.includes('unique')) {
            return { category: ErrorCategory.CONFLICT, code: ErrorCode.DB_CONFLICT, statusCode: 409 };
        }

        // Validation errors with detailed codes
        if (statusNum === 400 || statusNum === 422 ||
            message.includes('invalid') || message.includes('validation') ||
            message.includes('required') || message.includes('constraint')) {
            let code = ErrorCode.VALIDATION_FORMAT;
            if (message.includes('required') || message.includes('missing')) {
                code = ErrorCode.VALIDATION_REQUIRED;
            } else if (message.includes('constraint') || message.includes('foreign key')) {
                code = ErrorCode.VALIDATION_CONSTRAINT;
            }
            return { category: ErrorCategory.VALIDATION, code, statusCode: statusNum || 400 };
        }

        // Permission errors
        if (message.includes('permission') || message.includes('denied') ||
            message.includes('policy') || message.includes('rls')) {
            return { category: ErrorCategory.PERMISSION, code: ErrorCode.UNKNOWN, statusCode: 403 };
        }

        // Network errors (fetch failures)
        if (message.includes('network') || message.includes('fetch') ||
            message.includes('connection') || message.includes('econnrefused') ||
            message.includes('enotfound') || message.includes('offline') ||
            (err.name === 'TypeError' && message.includes('failed'))) {
            let code = ErrorCode.NETWORK_OFFLINE;
            if (message.includes('dns') || message.includes('enotfound')) {
                code = ErrorCode.NETWORK_DNS;
            } else if (message.includes('ssl') || message.includes('certificate')) {
                code = ErrorCode.NETWORK_SSL;
            }
            return { category: ErrorCategory.NETWORK, code };
        }

        // Server errors with detailed codes
        if (statusNum >= 500) {
            let code = ErrorCode.SERVER_INTERNAL;
            if (statusNum === 502) {
                code = ErrorCode.SERVER_BAD_GATEWAY;
            } else if (statusNum === 503) {
                code = ErrorCode.SERVER_UNAVAILABLE;
            }
            return { category: ErrorCategory.SERVER_ERROR, code, statusCode: statusNum };
        }
    }

    return { category: ErrorCategory.UNKNOWN, code: ErrorCode.UNKNOWN };
}

/**
 * Create a standardized AppError from any error
 */
export function createAppError(
    error: unknown,
    context?: string,
    options?: {
        correlationId?: string;
        metadata?: Record<string, unknown>;
    }
): AppError {
    const { category, code, statusCode } = categorizeError(error);

    // Extract technical message
    let technicalMessage = 'Unknown error';
    if (error instanceof Error) {
        technicalMessage = error.message;
    } else if (error && typeof error === 'object') {
        const err = error as Record<string, unknown>;
        technicalMessage = String(err.message ?? err.error ?? JSON.stringify(error));
    } else if (typeof error === 'string') {
        technicalMessage = error;
    }

    // Add context to technical message
    if (context) {
        technicalMessage = `[${context}] ${technicalMessage}`;
    }

    // Determine if error is retryable
    const retryable =
        category === ErrorCategory.NETWORK ||
        category === ErrorCategory.RATE_LIMIT ||
        category === ErrorCategory.TIMEOUT ||
        category === ErrorCategory.SERVER_ERROR;

    const appError: AppError = {
        category,
        code,
        message: USER_MESSAGES[category],
        technicalMessage,
        retryable,
        statusCode,
        correlationId: options?.correlationId ?? undefined,
        timestamp: Date.now(),
        context,
        metadata: options?.metadata,
        originalError: error,
    };

    // Record in metrics
    recordErrorMetric(appError);

    return appError;
}

/**
 * Handle an error with logging and optional callback
 * This is the main entry point for error handling in services
 */
export function handleError(
    error: unknown,
    context: string,
    options?: {
        silent?: boolean;        // Don't log to console
        rethrow?: boolean;       // Re-throw after handling (default: true)
        correlationId?: string;  // Request correlation ID
        onError?: (appError: AppError) => void;  // Custom error handler
    }
): AppError {
    const appError = createAppError(error, context, {
        correlationId: options?.correlationId,
    });
    const { silent = false, rethrow = true, onError } = options ?? {};

    // Log the error using structured logger
    if (!silent) {
        logger.error(appError.technicalMessage, error instanceof Error ? error : undefined, {
            category: appError.category,
            retryable: appError.retryable,
            statusCode: appError.statusCode,
        });
    }

    // Call custom error handler if provided
    if (onError) {
        onError(appError);
    }

    // Re-throw if requested (default behavior for services)
    if (rethrow) {
        throw appError;
    }

    return appError;
}

/**
 * Default retry configuration for database operations.
 * Shared across all service files to ensure consistent retry behavior.
 */
export const DEFAULT_RETRY_CONFIG = {
    maxAttempts: 3,
    baseDelayMs: 500,
    maxDelayMs: 5000,
} as const;

/**
 * Retry configuration
 */
export interface RetryConfig {
    maxAttempts?: number;      // Default: 3
    baseDelayMs?: number;      // Default: 1000
    maxDelayMs?: number;       // Default: 10000
    shouldRetry?: (error: AppError, attempt: number) => boolean;
    useCircuitBreaker?: boolean;  // Default: false
    circuitBreakerConfig?: CircuitBreakerConfig;
    correlationId?: string;    // Request correlation ID
    onRetry?: (error: AppError, attempt: number) => void;  // Callback on retry
}

/**
 * Execute an async function with retry logic and optional circuit breaker
 * Uses exponential backoff for transient failures
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    context: string,
    config?: RetryConfig
): Promise<T> {
    const {
        maxAttempts = 3,
        baseDelayMs = 1000,
        maxDelayMs = 10000,
        shouldRetry = (error: AppError) => error.retryable,
        useCircuitBreaker = false,
        circuitBreakerConfig = DEFAULT_CIRCUIT_CONFIG,
        correlationId,
        onRetry,
    } = config ?? {};

    // Check circuit breaker if enabled
    if (useCircuitBreaker && !canMakeRequest(context, circuitBreakerConfig)) {
        const circuitError = createAppError(
            { message: 'Circuit breaker is open', code: 'CIRCUIT_OPEN' },
            context,
            { correlationId }
        );
        circuitError.category = ErrorCategory.CIRCUIT_OPEN;
        circuitError.code = ErrorCode.CIRCUIT_OPEN;
        circuitError.retryable = false;
        throw circuitError;
    }

    let lastError: AppError | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const result = await fn();

            // Record success for circuit breaker
            if (useCircuitBreaker) {
                recordSuccess(context, circuitBreakerConfig);
            }

            return result;
        } catch (error) {
            lastError = createAppError(error, context, { correlationId });

            // Record failure for circuit breaker
            if (useCircuitBreaker) {
                recordFailure(context, circuitBreakerConfig);
            }

            // Check if we should retry
            if (attempt < maxAttempts && shouldRetry(lastError, attempt)) {
                // Calculate delay with exponential backoff and jitter
                const delay = Math.min(
                    baseDelayMs * Math.pow(2, attempt - 1) + Math.random() * 1000,
                    maxDelayMs
                );

                logger.warn(`Retry attempt ${attempt}/${maxAttempts} for ${context}`, {
                    category: lastError.category,
                    code: lastError.code,
                    correlationId: lastError.correlationId,
                    delayMs: Math.round(delay),
                });

                // Call onRetry callback if provided
                if (onRetry) {
                    onRetry(lastError, attempt);
                }

                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                // Don't retry - log and throw
                logger.error(`Failed after ${attempt} attempt(s): ${context}`, undefined, {
                    category: lastError.category,
                    code: lastError.code,
                    correlationId: lastError.correlationId,
                    attempts: attempt,
                });
                throw lastError;
            }
        }
    }

    // This shouldn't be reached, but TypeScript needs it
    throw lastError;
}

/**
 * Type guard to check if an error is an AppError
 */
export function isAppError(error: unknown): error is AppError {
    return (
        error !== null &&
        typeof error === 'object' &&
        'category' in error &&
        'message' in error &&
        'technicalMessage' in error &&
        'retryable' in error
    );
}

/**
 * Get a user-friendly message from any error
 */
export function getUserMessage(error: unknown): string {
    if (isAppError(error)) {
        return error.message;
    }
    return createAppError(error).message;
}

/**
 * Check if an error is retryable
 */
export function isRetryable(error: unknown): boolean {
    if (isAppError(error)) {
        return error.retryable;
    }
    return createAppError(error).retryable;
}

/**
 * Wrap a service function with standardized error handling
 * Useful for creating consistent error handling across all service methods
 */
export function withErrorHandling<TArgs extends unknown[], TResult>(
    fn: (...args: TArgs) => Promise<TResult>,
    context: string
): (...args: TArgs) => Promise<TResult> {
    return async (...args: TArgs): Promise<TResult> => {
        try {
            return await fn(...args);
        } catch (error) {
            handleError(error, context);
            // handleError throws by default, but TypeScript doesn't know that
            throw error;
        }
    };
}

// ============================================================================
// BATCH ERROR HANDLING
// ============================================================================

export interface BatchResult<T> {
    success: boolean;
    data?: T;
    error?: AppError;
    index: number;
}

export interface BatchSummary<T> {
    totalCount: number;
    successCount: number;
    failureCount: number;
    results: BatchResult<T>[];
    errors: AppError[];
    correlationId: string;
}

/**
 * Execute multiple async operations with error isolation
 * Each operation's failure doesn't affect others
 */
export async function withBatchErrorHandling<T, TArg>(
    items: TArg[],
    fn: (item: TArg, index: number) => Promise<T>,
    context: string,
    options?: {
        continueOnError?: boolean;    // Continue processing after error (default: true)
        maxConcurrent?: number;       // Max concurrent operations (default: 5)
        correlationId?: string;
    }
): Promise<BatchSummary<T>> {
    const {
        continueOnError = true,
        maxConcurrent = 5,
        correlationId = generateCorrelationId(),
    } = options ?? {};

    const results: BatchResult<T>[] = [];
    const errors: AppError[] = [];

    // Process in batches for concurrency control
    for (let i = 0; i < items.length; i += maxConcurrent) {
        const batch = items.slice(i, i + maxConcurrent);
        const batchPromises = batch.map(async (item, batchIndex) => {
            const index = i + batchIndex;
            try {
                const data = await fn(item, index);
                return { success: true, data, index } as BatchResult<T>;
            } catch (error) {
                const appError = createAppError(error, `${context}[${index}]`, {
                    correlationId,
                    metadata: { batchIndex: index, item },
                });
                errors.push(appError);

                if (!continueOnError) {
                    throw appError;
                }

                return { success: false, error: appError, index } as BatchResult<T>;
            }
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
    }

    const successCount = results.filter(r => r.success).length;

    logger.info(`Batch operation completed: ${context}`, {
        correlationId,
        totalCount: items.length,
        successCount,
        failureCount: errors.length,
    });

    return {
        totalCount: items.length,
        successCount,
        failureCount: errors.length,
        results,
        errors,
        correlationId,
    };
}

/**
 * Execute operations with automatic retry per item
 */
export async function withBatchRetry<T, TArg>(
    items: TArg[],
    fn: (item: TArg, index: number) => Promise<T>,
    context: string,
    options?: {
        retryConfig?: RetryConfig;
        maxConcurrent?: number;
        correlationId?: string;
    }
): Promise<BatchSummary<T>> {
    const {
        retryConfig,
        maxConcurrent = 5,
        correlationId = generateCorrelationId(),
    } = options ?? {};

    return withBatchErrorHandling(
        items,
        async (item, index) => {
            return withRetry(
                () => fn(item, index),
                `${context}[${index}]`,
                { ...retryConfig, correlationId }
            );
        },
        context,
        { maxConcurrent, correlationId }
    );
}

// ============================================================================
// ERROR AGGREGATION UTILITIES
// ============================================================================

/**
 * Aggregate multiple errors into a single error
 */
export function aggregateErrors(errors: AppError[], context: string): AppError {
    if (errors.length === 0) {
        return createAppError({ message: 'No errors to aggregate' }, context);
    }

    if (errors.length === 1) {
        return errors[0];
    }

    // Determine the most severe category
    const categoryPriority: Record<ErrorCategory, number> = {
        [ErrorCategory.AUTH]: 10,
        [ErrorCategory.PERMISSION]: 9,
        [ErrorCategory.CIRCUIT_OPEN]: 8,
        [ErrorCategory.SERVER_ERROR]: 7,
        [ErrorCategory.NETWORK]: 6,
        [ErrorCategory.TIMEOUT]: 5,
        [ErrorCategory.RATE_LIMIT]: 4,
        [ErrorCategory.CONFLICT]: 3,
        [ErrorCategory.NOT_FOUND]: 2,
        [ErrorCategory.VALIDATION]: 1,
        [ErrorCategory.UNKNOWN]: 0,
    };

    const sortedErrors = [...errors].sort(
        (a, b) => (categoryPriority[b.category] || 0) - (categoryPriority[a.category] || 0)
    );

    const primaryError = sortedErrors[0];

    return {
        ...primaryError,
        message: `${errors.length} errors occurred. ${primaryError.message}`,
        technicalMessage: `[${context}] ${errors.length} errors: ${errors.map(e => e.technicalMessage).join('; ')}`,
        metadata: {
            ...primaryError.metadata,
            errorCount: errors.length,
            allCategories: [...new Set(errors.map(e => e.category))],
            allCodes: [...new Set(errors.map(e => e.code))],
        },
    };
}

// ============================================================================
// TYPED ERROR CREATION HELPERS
// ============================================================================

/**
 * Create a validation error
 */
export function createValidationError(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>
): AppError {
    const error = createAppError({ message }, context, { metadata });
    error.category = ErrorCategory.VALIDATION;
    error.code = ErrorCode.VALIDATION_FORMAT;
    error.message = message; // Use the custom message
    error.retryable = false;
    return error;
}

/**
 * Create a not found error
 */
export function createNotFoundError(
    resourceType: string,
    resourceId?: string,
    context?: string
): AppError {
    const message = resourceId
        ? `${resourceType} with ID '${resourceId}' not found`
        : `${resourceType} not found`;
    const error = createAppError({ message }, context);
    error.category = ErrorCategory.NOT_FOUND;
    error.code = ErrorCode.UNKNOWN;
    error.message = 'The requested item could not be found.';
    error.retryable = false;
    return error;
}

/**
 * Create a permission error
 */
export function createPermissionError(
    action: string,
    context?: string
): AppError {
    const error = createAppError({ message: `Permission denied: ${action}` }, context);
    error.category = ErrorCategory.PERMISSION;
    error.code = ErrorCode.UNKNOWN;
    error.message = "You don't have permission to perform this action.";
    error.retryable = false;
    return error;
}

/**
 * Create a timeout error
 */
export function createTimeoutError(
    operation: string,
    timeoutMs: number,
    context?: string
): AppError {
    const error = createAppError(
        { message: `Operation '${operation}' timed out after ${timeoutMs}ms` },
        context
    );
    error.category = ErrorCategory.TIMEOUT;
    error.code = ErrorCode.NETWORK_TIMEOUT;
    error.message = 'The request took too long. Please try again.';
    error.retryable = true;
    return error;
}
